/**
 * The per-connection roster for a connector's detail (Flow L Frame 2 §5 →
 * Frame 3; ADR-035 / S6.5 + S6.5a/b).
 *
 * Lazy-fetches the owning component's connections (:func:`useComponentConnections`,
 * REQ-STATUS-021, bounded server-side at the 1,000-connection ceiling), then
 * **groups by aggregator** (``aggregator_id``; storyboard OQ-3), sorts
 * **worst-first** (worst group first, worst connection within a group), offers a
 * **search** filter, and renders through a lightweight **fixed-row virtual list**
 * so it stays smooth at the 150→1,000+ range. A group header for a real
 * aggregator carries the re-homed incident-disable action (S6.5b) wired to that
 * ``aggregator_id``; the direct/ungrouped bucket (null aggregator) has none.
 *
 * Since REQ-STATUS-044 as amended 2026-07-31, a row **expands in place to a
 * drawer** carrying that connection's detail instead of promoting it to its own
 * selection — a disclosure inside whatever Selection Detail hosts the roster
 * (component-level on ``Ingress``, interface-level on egress). The windowing
 * survives that because :func:`VirtualList` measures in whole fixed-height
 * **slots**, and an open drawer is simply an item spanning several of them.
 *
 * # No new dependency (CLAUDE.md §4.3)
 *
 * The windowing is a ~25-line in-house :func:`VirtualList` (fixed row height,
 * scroll-driven visible-range slice) rather than a new third-party dep
 * (react-window / @tanstack/react-virtual). The grouped roster flattens to a
 * uniform-height row list (``[header, conn, conn, header, …]``) — the simplest
 * case virtualization handles — so the in-house version is sufficient and avoids
 * the tech-stack / LICENSING cascade.
 *
 * Cross-references:
 *   - Flow L §5 (connection list + detail); storyboard OQ-3 (virtualize / sort /
 *     search / group-by-aggregator).
 *   - REQ-STATUS-021 (connections endpoint); REQ-STATUS-029 (support_engineer
 *     disable); REQ-UI-059 (keyboard).
 */
import { useMemo, useState } from "react";
import {
  parseRollupState,
  rollupStateMeta,
  type StatusConnection,
} from "@/system-status/status-model";
import { useComponentConnections } from "@/system-status/use-component-connections";
import {
  flattenGroups,
  formatBytes,
  groupConnectionsByAggregator,
  type ConnectionGroup,
  type RosterRow,
} from "@/system-status/status-connection-format";
import { cn } from "@/lib/utils";

/** Row height (px) for the virtual list — headers + connection rows share it. */
const ROW_HEIGHT = 46;

/**
 * Slots an open connection drawer occupies (REQ-STATUS-044 per-row drawer).
 *
 * A **fixed** budget, with the drawer's own body scrolling inside it, rather
 * than a measured height. Measuring would make row offsets depend on layout the
 * virtual list computes *before* the browser has laid the drawer out, which is
 * the classic variable-height virtualization loop; a fixed budget keeps the
 * scroll height exactly right at every count (see the honesty test) at the cost
 * of an inner scrollbar on a tall drawer.
 */
const DRAWER_ROWS = 8;

/**
 * Prefix offsets, in slots, for a row list whose items may span several slots.
 * ``offsets[i]`` is the first slot of item ``i``; ``offsets[items.length]`` is
 * the total slot count.
 */
function slotOffsets(spans: readonly number[]): number[] {
  const offsets = new Array<number>(spans.length + 1);
  offsets[0] = 0;
  for (let i = 0; i < spans.length; i += 1) offsets[i + 1] = offsets[i] + spans[i];
  return offsets;
}

/** Index of the item containing ``slot`` (binary search over prefix offsets). */
function itemAtSlot(offsets: readonly number[], slot: number): number {
  let lo = 0;
  let hi = offsets.length - 2; // last item index
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (offsets[mid] <= slot) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * A lightweight virtual list (no third-party dep). Renders only the rows in (or
 * near) the scroll viewport; the outer spacer preserves the full scroll height
 * so the scrollbar is accurate for the whole roster.
 *
 * Rows are uniform height by default. ``rowSpanOf`` lets one item occupy N
 * slots — how an expanded connection drawer (REQ-STATUS-044) is represented
 * without abandoning virtualization: the geometry stays a whole number of
 * fixed-height slots, so offsets remain exactly computable and the spacer stays
 * honest. Omitting it reproduces the fixed-row behaviour exactly
 * (``offsets[i] === i``).
 */
export function VirtualList<T>({
  items,
  rowHeight = ROW_HEIGHT,
  height,
  overscan = 4,
  rowSpanOf,
  renderRow,
}: {
  items: readonly T[];
  rowHeight?: number;
  height: number;
  overscan?: number;
  /** Slots this item occupies (default 1). Must be ≥ 1. */
  rowSpanOf?: (item: T, index: number) => number;
  renderRow: (item: T, index: number) => React.ReactNode;
}): React.ReactElement {
  const [scrollTop, setScrollTop] = useState(0);
  const offsets = useMemo(
    () => slotOffsets(items.map((item, i) => Math.max(1, rowSpanOf?.(item, i) ?? 1))),
    [items, rowSpanOf],
  );
  const totalSlots = offsets[offsets.length - 1];
  const visibleCount = Math.max(1, Math.ceil(height / rowHeight));
  const firstSlot = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const lastSlot = firstSlot + visibleCount + 2 * overscan;
  const start = items.length === 0 ? 0 : itemAtSlot(offsets, firstSlot);
  const end = items.length === 0 ? 0 : Math.min(items.length, itemAtSlot(offsets, lastSlot) + 1);
  const slice = items.slice(start, end);
  return (
    <div
      data-testid="connection-virtual-list"
      style={{ height, overflowY: "auto", position: "relative" }}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
    >
      <div style={{ height: totalSlots * rowHeight, position: "relative" }}>
        {slice.map((item, i) => (
          <div
            key={start + i}
            style={{
              position: "absolute",
              top: offsets[start + i] * rowHeight,
              left: 0,
              right: 0,
              height: (offsets[start + i + 1] - offsets[start + i]) * rowHeight,
            }}
          >
            {renderRow(item, start + i)}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * A group header row — the aggregator's worst-state dot + label + count, and (for
 * a real aggregator) carried the incident-disable action until REQ-UI-164 retired
 * it (ADR-040 R-2). The successor is a provider **stop**, which lives on the
 * provider's own surface rather than on a connection roster — a kill switch
 * belongs beside the thing it stops, not beside a list of what that thing is
 * currently doing.
 */
function GroupHeader({ group }: { group: ConnectionGroup }): React.ReactElement {
  const meta = rollupStateMeta(group.worstState);
  const count = group.connections.length;
  const key = group.aggregatorId ?? "ungrouped";
  return (
    <div
      data-testid={`connection-group-${key}`}
      className="flex h-full w-full items-center gap-2 border-b border-border bg-bg-elevated px-3"
    >
      <span
        aria-hidden="true"
        className="inline-block h-2 w-2 flex-none rounded-full"
        style={{ background: meta.dotColor }}
      />
      <span className="min-w-0 flex-1 truncate text-body-sm font-medium text-text-primary">
        {group.label}
      </span>
      <span className="flex-none text-meta text-text-muted">
        {count} conn{count === 1 ? "" : "s"}
      </span>
    </div>
  );
}

/**
 * One connection roster row — state mark + peer + activity + rate.
 *
 * In **drawer mode** (``expanded`` non-null) the row is a disclosure control:
 * it reports ``aria-expanded`` and renders the drawer body underneath itself,
 * inside the slot budget the virtual list reserved. In **select mode**
 * (``expanded === null``) it is the pre-hierarchy control that promoted the
 * connection to its own bus selection, reporting ``aria-pressed``. The two
 * ARIA idioms are deliberately different because the two behaviours are: one
 * discloses detail in place, the other moves the operator's focus.
 */
function ConnectionRow({
  connection,
  selected,
  expanded,
  onActivate,
  drawer,
}: {
  connection: StatusConnection;
  selected: boolean;
  /** ``null`` in select mode; the disclosure state in drawer mode. */
  expanded: boolean | null;
  onActivate: () => void;
  drawer?: React.ReactNode;
}): React.ReactElement {
  const state = parseRollupState(connection.rollup_state);
  const meta = rollupStateMeta(state);
  const active = connection.conn_state === "connected";
  const drawerId = `connection-drawer-${connection.connection_id}`;
  const row = (
    <button
      type="button"
      onClick={onActivate}
      data-testid={`connection-row-${connection.connection_id}`}
      aria-label={`${connection.peer_label}, ${meta.label}`}
      {...(expanded === null
        ? { "aria-pressed": selected }
        : { "aria-expanded": expanded, "aria-controls": drawerId })}
      // In drawer mode the row is one slot of a taller block, so its height is
      // pinned to ROW_HEIGHT rather than `h-full` — resolved from the constant,
      // never restated as a utility class, so a change to the row height cannot
      // desynchronise the drawn row from the slot the virtual list reserved.
      style={expanded === null ? undefined : { height: ROW_HEIGHT }}
      className={cn(
        "flex w-full items-center gap-2 border-b border-border py-0 pl-6 pr-4 text-left text-body-sm",
        expanded === null ? "h-full" : "flex-none",
        "hover:bg-bg-elevated focus:bg-bg-elevated focus:outline-none",
        (selected || expanded === true) && "bg-bg-elevated",
      )}
    >
      <span
        aria-hidden="true"
        className="inline-block h-2.5 w-2.5 flex-none rounded-full"
        style={{ background: meta.dotColor }}
      />
      <span className="min-w-0 flex-1 truncate font-medium text-text-primary">
        {connection.peer_label}
      </span>
      <span className="flex-none text-meta text-text-secondary">
        {active ? connection.activity : connection.conn_state}
      </span>
      <span className="w-24 flex-none text-right font-mono text-meta text-text-muted">
        {active ? `${formatBytes(connection.bytes_per_sec)}/s` : "—"}
      </span>
    </button>
  );
  if (expanded !== true) return row;
  return (
    <div className="flex h-full flex-col">
      {row}
      <div
        id={drawerId}
        data-testid={drawerId}
        // The drawer scrolls inside its fixed slot budget — see DRAWER_ROWS.
        className="min-h-0 flex-1 overflow-y-auto border-b border-border bg-bg-surface"
      >
        {drawer}
      </div>
    </div>
  );
}

/**
 * The connection roster for one component. Search + group-by-aggregator +
 * worst-first + virtualized rows; each aggregator group header carries the
 * incident-disable (S6.5b).
 *
 * # Two row behaviours, and which one is current
 *
 * - **Drawer mode** (``renderDrawer`` supplied) — REQ-STATUS-044 as amended
 *   2026-07-31: a row expands **in place** to a drawer carrying that
 *   connection's detail. The expansion is a *disclosure* inside whatever
 *   Selection Detail hosts the roster, so it does **not** touch the selection
 *   bus and the host entity remains the single focus (ADR-024 §5 / REQ-STATUS-039).
 *   One drawer at a time: the drawer body renders section shells whose DOM ids
 *   are derived from a fixed section id, so two open at once would duplicate ids.
 * - **Select mode** (``onSelect`` only) — the pre-hierarchy behaviour, where a
 *   row promoted the connection to its own ``status-connection`` bus selection.
 *   Retained because the kind is part of a deployed contract (v1.0.0) and
 *   nothing about it is wrong; the hierarchy simply gave the roster a better
 *   home. Both props may be supplied; ``renderDrawer`` wins.
 */
export function ConnectionList({
  componentId,
  selectedConnectionId = null,
  onSelect,
  renderDrawer,
  restrictToInterface,
  height = 320,
}: {
  componentId: string;
  /** Highlighted row in select mode. Ignored in drawer mode. */
  selectedConnectionId?: string | null;
  /** Select mode: promote the clicked connection to its own bus selection. */
  onSelect?: (connection: StatusConnection) => void;
  /** Drawer mode: render the expanded connection's detail in place. */
  renderDrawer?: (connection: StatusConnection) => React.ReactNode;
  /**
   * Show only the connections belonging to this interface (REQ-STATUS-086).
   *
   * For callers reading a **peer's** component rather than an interface's. A
   * peer report carries every interface's connections in one flat list, and the
   * collector persists no `interface_id` to split them by — but the emitter
   * builds each `connection_id` as ``f"{interface}|{endpoint}"``
   * (`_interface_status.py`), so the interface name survives as a prefix and is
   * the available discriminator.
   *
   * ⚠ Matched as a **prefix up to the first `|`**, never a bare
   * ``startsWith``: an interface called `sapient` would otherwise also claim
   * `sapient-xml`'s rows, which is the misattribution this exists to prevent.
   */
  restrictToInterface?: string;
  height?: number;
}): React.ReactElement {
  const query = useComponentConnections(componentId);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const drawerMode = renderDrawer !== undefined;

  const rows = useMemo<RosterRow[]>(() => {
    const all = query.data?.connections ?? [];
    // Interface restriction first, so the operator's search runs over the rows
    // that belong here rather than over the whole peer's.
    const mine =
      restrictToInterface === undefined
        ? all
        : all.filter((c) => c.connection_id.split("|", 1)[0] === restrictToInterface);
    const term = search.trim().toLowerCase();
    const filtered =
      term === "" ? mine : mine.filter((c) => c.peer_label.toLowerCase().includes(term));
    return flattenGroups(groupConnectionsByAggregator(filtered));
  }, [query.data, search, restrictToInterface]);

  // A row that scrolls out of the roster (filtered away, or aged out of the
  // feed) must not leave a phantom expansion behind that re-opens when it
  // returns; the span function below already reads `expandedId` per render, so
  // the only thing needed is that the id stops matching — which it does.
  const rowSpanOf = useMemo(
    () => (row: RosterRow) =>
      drawerMode && row.kind === "connection" && row.connection.connection_id === expandedId
        ? 1 + DRAWER_ROWS
        : 1,
    [drawerMode, expandedId],
  );

  if (query.isLoading) {
    return (
      <p role="status" className="px-4 py-3 text-body-sm text-text-secondary">
        Loading connections…
      </p>
    );
  }
  if (query.isError) {
    return (
      <p role="alert" className="px-4 py-3 text-body-sm text-destructive">
        Could not load connections: {query.error.message}
      </p>
    );
  }
  const total = query.data?.connections.length ?? 0;
  if (total === 0) {
    return <p className="px-4 py-3 text-body-sm text-text-muted">No connections on this edge.</p>;
  }

  return (
    <div data-testid="connection-list" className="flex flex-col">
      <div className="px-4 pb-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Filter ${total} connection${total === 1 ? "" : "s"}…`}
          data-testid="connection-search"
          aria-label="Filter connections by peer"
          className="w-full rounded border border-border bg-bg-base px-2 py-1 text-body-sm text-text-primary"
        />
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-body-sm text-text-muted">No connections match the filter.</p>
      ) : (
        <VirtualList
          items={rows}
          height={height}
          rowSpanOf={rowSpanOf}
          renderRow={(row) =>
            row.kind === "group" ? (
              <GroupHeader group={row.group} />
            ) : (
              <ConnectionRow
                connection={row.connection}
                selected={row.connection.connection_id === selectedConnectionId}
                expanded={drawerMode ? row.connection.connection_id === expandedId : null}
                onActivate={() => {
                  if (drawerMode) {
                    // Accordion: opening one closes any other (see the id note
                    // in the component docstring).
                    setExpandedId((open) =>
                      open === row.connection.connection_id ? null : row.connection.connection_id,
                    );
                    return;
                  }
                  onSelect?.(row.connection);
                }}
                drawer={drawerMode ? renderDrawer(row.connection) : undefined}
              />
            )
          }
        />
      )}
    </div>
  );
}
